// Libraries
import { FC } from 'react';
import { Grid } from '@mui/material';
import { Tooltip } from '@cb/apricot-react';

// Interfaces & Typing
import { DeepPartial, Draft } from 'src/types/common';
import { IAppForm } from 'src/types/org/common/appFormSchema';
import { IDI } from 'src/types/org/di/diInterface';
import { IFieldErrors } from 'src/types/validation';
import { IOrgModel } from 'src/types/org/orgInterface';
import { usStateOptions, instLvlOptions, KEY_OTHER, KEY_US_GOV, TYPE_OPTIONS } from 'src/constants';

// Components
import { Typography } from 'src/components/common';
import { AddressFields } from 'src/generic/component/address';
import { ContactFields } from 'src/generic/component/contact';
import { FieldRow, FieldLabel, Panel, Title, RadioLabel } from 'src/generic/component';
import { BooleanRadio, Input, TextArea, Checkboxes, Checkbox, Dropdown } from 'src/generic/component/form';
import { SupportDocFields } from '../components/SupportDoc';

// Processing
import { ErrorManager } from 'src/types/validation';
import { IData } from 'src/types/org/common/dataSchema';

const tooltipId = 'toolTipTextId';

interface IProps<T> {
  readOnly?: boolean;
  item: Draft<T> | null;
  errors: IFieldErrors;
  onUpdate?: (item: Draft<T>) => void;
}

const InstitutionDetails: FC<IProps<IAppForm>> = ({ item: initial, errors, readOnly, onUpdate }) => {
  const { getError, hasError } = ErrorManager(errors);
  const {
    existingOrg,
    existingOrgInfo,
    mergeList,
    instTypeOther,
    congressDistrict,
    stateRep,
    incumbent,
    reasonForApplication,
  } = initial || {};

  const instType: string[] = initial?.instType || [];

  const changed = (updates: Draft<IAppForm>) => {
    if (onUpdate) onUpdate({ ...initial, ...updates });
  };

  return (
    <>
      <FieldRow>
        <FieldLabel variant="body1" style={{ marginBottom: '4px' }}>
          Institution Type (Check all that apply)
        </FieldLabel>
        <Checkboxes
          readOnly={readOnly}
          error={getError('instType')}
          value={instType}
          options={TYPE_OPTIONS.map((val: string) => ({ key: val, label: val }))}
          onChange={(values: string[]) => {
            changed({
              instType: values,
              instTypeOther: values.includes(KEY_OTHER) ? instTypeOther : undefined,
              stateRep: values.includes(KEY_US_GOV) ? stateRep : undefined,
              congressDistrict: values.includes(KEY_US_GOV) ? congressDistrict : undefined,
              incumbent: values.includes(KEY_US_GOV) ? incumbent : undefined
            });
          }}
        />
      </FieldRow>
      {instType.includes(KEY_OTHER) && (
        <FieldRow>
          <FieldLabel variant="body1">Specify the institution type that isn't represented in the options:</FieldLabel>
          <Input
            disabled={readOnly}
            value={instTypeOther}
            validation={hasError('instTypeOther') ? 'error' : undefined}
            validationMsg={getError('instTypeOther')}
            onChange={(e) => {
              changed({ instTypeOther: e.target.value });
            }}
          />
        </FieldRow>
      )}
      {instType.includes('US Government Official') && (
        <FieldRow>
          <Typography variant="body1">State Represented</Typography>
          <Dropdown
            disabled={readOnly}
            values={usStateOptions.filter((item) => item.primary === true).map((option) => ({ key: option.value, label: option.label }))}
            validation={hasError('stateRep') ? 'error' : undefined}
            validationMsg={getError('stateRep')}
            onChange={(e) => {
              changed({ stateRep: e.target.value });
            }}
            value={stateRep}
          />
          <FieldLabel variant="body1">Congressional District</FieldLabel>
          <Input
            disabled={readOnly}
            value={congressDistrict}
            validation={hasError('congressDistrict') ? 'error' : undefined}
            validationMsg={getError('congressDistrict')}
            onChange={(e) => {
              changed({ congressDistrict: e.target.value });
            }}
          />
          <FieldLabel variant="body1">Incumbent</FieldLabel>
          <Input
            disabled={readOnly}
            value={incumbent}
            validation={hasError('incumbent') ? 'error' : undefined}
            validationMsg={getError('incumbent')}
            onChange={(e) => {
              changed({ incumbent: e.target.value });
            }}
          />
        </FieldRow>
      )}
      <FieldRow>
        <FieldLabel variant="body1">
          Please explain your institution's need for a College Board Score Send Code.
        </FieldLabel>
        <TextArea
          disabled={readOnly}
          validation={hasError('reasonForApplication') ? 'error' : undefined}
          validationMsg={getError('reasonForApplication')}
          onChange={(e: any) => {
            changed({ reasonForApplication: e.target.value });
          }}
          value={reasonForApplication}
        />
      </FieldRow>
      <FieldRow>
        <RadioLabel variant="body1">
          Has your organization ever used another name, address or College Board score send code?
        </RadioLabel>
        <BooleanRadio
          readOnly={readOnly}
          error={getError('existingOrg')}
          value={existingOrg}
          name="existingOrg"
          onChange={(value) => {
            changed({
              existingOrg: value,
              existingOrgInfo: value ? existingOrgInfo : undefined,
              mergeList: value ? mergeList : undefined,
            });
          }}
        />
      </FieldRow>
      {existingOrg && (
        <>
          <FieldRow>
            <FieldLabel variant="body1">Enter old organization information here</FieldLabel>
            <Input
              disabled={readOnly}
              validation={hasError('existingOrgInfo') ? 'error' : undefined}
              validationMsg={getError('existingOrgInfo')}
              onChange={(e) => {
                changed({ existingOrgInfo: e.target.value });
              }}
              value={existingOrgInfo}
            />
          </FieldRow>
          <FieldRow>
            <FieldLabel variant="body1">If a merger, list all organizations/codes affected</FieldLabel>
            <Input
              disabled={readOnly}
              validation={hasError('mergeList') ? 'error' : undefined}
              validationMsg={getError('mergeList')}
              onChange={(e) => {
                changed({ mergeList: e.target.value });
              }}
              value={mergeList}
            />
          </FieldRow>
        </>
      )}
    </>
  );
};

export interface IDICreateFieldProps {
  categoryId: string;
  userId: string;
  readOnly: boolean;
  item: Draft<IData>;
  validating?: boolean;
  errors: IFieldErrors;
  onUpdate?: (address: Draft<IData>) => void;
}

export const DICreateFields: FC<IDICreateFieldProps> = ({
  categoryId,
  item: initial,
  userId,
  errors,
  readOnly,
  onUpdate,
}) => {
  /**
   * Form Variables and Monitoring Funcationality
   *
   * This is the place where most additions and modifications will occur for the form
   * in addition to the rendering block.
   */

  const appForm: Draft<IAppForm> = initial.appForm || {};
  const org: Draft<DeepPartial<IOrgModel>> = initial.org || {};
  const di: Draft<IDI> = initial.di || {};

  const { hasError, getError, getFieldError } = ErrorManager(errors);

  const changed = (updates: Draft<IData>) => {
    if (onUpdate) onUpdate({ ...initial, ...updates });
  };

  const orgChanged = (updates: Draft<IOrgModel>) => {
    const data = {
      ...initial,
      org: {
        ...org,
        ...updates,
      },
    };
    if (onUpdate) onUpdate(data);
  };

  const diChanged = (updates: Draft<IDI>) => {
    const data = {
      ...initial,
      di: {
        ...di,
        ...updates,
      },
    };
    if (onUpdate) onUpdate(data);
  };

  const appChanged = (updates: Draft<IAppForm>) => {
    const data = {
      ...initial,
      appForm: {
        ...appForm,
        ...updates,
      },
    };
    if (onUpdate) onUpdate(data);
  };

  const {
    apEligibilityInd,
    apAddress,
    apContact,
    // clepEligibilityInd,
    // clepAddress,
    // clepContact,
    satEligibilityInd,
    satContact,
  } = di || {};

  const { visitingAddress, email, website, fax, phone, instLvlCd, name } = org || {};
  const { apElectronicRprt//, clepElectronicRprt 
  } = appForm || {};

  return (
    <>
      <Grid item xs={12}>
        <h3 style={{ marginBottom: '10px', }}>Create Request Form</h3>
        <div style={{ marginBottom: '10px', display: 'flex', flexDirection: 'column', width: 'fit-content', padding: '10px', border: '1px solid #324DC7' }}>
          <Typography variant="body1" style={{ fontWeight: 700, color: '#FF0000' }}>
            College Board Score Send Code requests must be submitted by:
          </Typography>
          Post Secondary Institutions:  Director of Admissions, Registrar, or Dean of Students
          <br />
          Government Official: Government Official
          <br />
          Scholarship: Director of Scholarship Program
          <br />
          <Typography variant="body1" style={{ fontWeight: 700, color: '#1E1E1E' }}>
            If the authorized submitter is unable to submit the request, he/she may designate a web-verifiable employee to submit on his/her behalf.
          </Typography>
          <Typography variant="body1" style={{ fontWeight: 700, color: '#1E1E1E' }}>
            Written authorization on letterhead must be included with the submission to continue processing.
          </Typography>
        </div>
      </Grid>
      <Panel>
        <Title>General Institution Information</Title>
        <Grid container spacing={5}>
          <Grid item xs={6}>
            <FieldRow>
              <FieldLabel variant="body1">Institution Name</FieldLabel>
              <Input
                disabled={readOnly}
                value={name}
                validation={hasError('org.name') ? 'error' : undefined}
                validationMsg={getError('org.name')}
                onChange={(e) => {
                  orgChanged({ name: e.target.value });
                }}
              />
            </FieldRow>
            <FieldRow>
              <FieldLabel variant="body1">Which of the following best describes the level of your institution?</FieldLabel>
              <Dropdown
                disabled={readOnly}
                values={instLvlOptions.map((option) => ({ key: option.value, label: option.label }))}
                validation={hasError('org.instLvlCd') ? 'error' : undefined}
                validationMsg={getError('org.instLvlCd')}
                onChange={(e) => {
                  orgChanged({ instLvlCd: parseInt(e.target.value) });
                }}
                value={instLvlCd?.toString()}
              />
            </FieldRow>
            <InstitutionDetails
              readOnly={readOnly}
              item={appForm}
              onUpdate={(values) => {
                changed({ appForm: values });
              }}
              errors={getFieldError('appForm')}
            />
            <FieldRow>
              <FieldLabel variant="body1" style={{ marginBottom: '4px' }}>
                Program Scores Accepted (Check all that apply)
              </FieldLabel>
              <div style={{ display: 'flex', flexDirection: 'column', rowGap: '5px' }}>
                <Checkbox
                  disabled={readOnly}
                  onChange={() =>
                    diChanged({
                      satEligibilityInd: !satEligibilityInd,
                      satContact: {},
                    })
                  }
                  label={'SAT'}
                  name={'satEligibilityInd'}
                  checked={satEligibilityInd}
                />
                <Checkbox
                  disabled={readOnly}
                  onChange={() =>
                    diChanged({
                      apEligibilityInd: !apEligibilityInd,
                      apContact: {},
                    })
                  }
                  label={'AP'}
                  name={'apEligibilityInd'}
                  checked={apEligibilityInd}
                  error={hasError('di.EligibilityInd') ? getError('di.EligibilityInd') : undefined}
                />
                {/* <Checkbox
                disabled={readOnly}
                  onChange={() =>
                    diChanged({
                      clepEligibilityInd: !clepEligibilityInd,
                      clepContact: {},
                    })
                  }
                  label={'CLEP'}
                  name={'clepEligibilityInd'}
                  checked={clepEligibilityInd}
                  style={{ marginBottom: '5px' }}
                /> */}
              </div>
            </FieldRow>
          </Grid>
          <Grid item xs={6}>
            <>
              <div id={tooltipId}>
                <FieldRow>
                  <strong>Visiting Address</strong>
                </FieldRow>
                <AddressFields
                  readOnly={readOnly}
                  errors={getFieldError('org.visitingAddress')}
                  address={visitingAddress}
                  onUpdate={(address) => {
                    orgChanged({ visitingAddress: address });
                  }}
                />
              </div>
              <Tooltip placement="bottom" trigger={tooltipId}>
                <p>{'Physical address for campus specified in this request.'}</p>
              </Tooltip>
              <FieldRow>
                <FieldLabel variant="body1">Phone</FieldLabel>
                <Input
                  disabled={readOnly}
                  validation={hasError('org.phone.formatted') || hasError('org.phone') ? 'error' : undefined}
                  validationMsg={getError('org.phone.formatted') || getError('org.phone')}
                  onChange={(e) => {
                    orgChanged({ phone: { formatted: e.target.value || undefined } });
                  }}
                  value={phone?.formatted || ''}
                />
              </FieldRow>
              <FieldRow>
                <FieldLabel variant="body1">Fax</FieldLabel>
                <Input
                  disabled={readOnly}
                  validation={hasError('org.fax.formatted') ? 'error' : undefined}
                  validationMsg={getError('org.fax.formatted')}
                  onChange={(e) => {
                    orgChanged({ fax: { formatted: e.target.value || undefined } });
                  }}
                  value={fax?.formatted || ''}
                />
              </FieldRow>
              <FieldRow>
                <FieldLabel variant="body1">Website</FieldLabel>
                <Input
                  disabled={readOnly}
                  validation={hasError('org.website') ? 'error' : undefined}
                  validationMsg={getError('org.website')}
                  onChange={(e) => {
                    orgChanged({ website: e.target.value || undefined });
                  }}
                  value={website || ''}
                />
              </FieldRow>
              <FieldRow>
                <FieldLabel variant="body1">Email</FieldLabel>
                <Input
                  disabled={readOnly}
                  validation={hasError('org.email') ? 'error' : undefined}
                  validationMsg={getError('org.email')}
                  onChange={(e) => {
                    orgChanged({ email: e.target.value || undefined });
                  }}
                  value={email || ''}
                />
              </FieldRow>
            </>
          </Grid>
        </Grid>
      </Panel>
      {satEligibilityInd && (
        <>
          <Panel>
            <Grid container spacing={5}>
              <Grid item xs={6}>
                <Title>SAT Program Contact Information</Title>
                <>
                  <ContactFields
                    readOnly={readOnly}
                    contact={satContact || {}}
                    onUpdate={(values) => {
                      diChanged({ satContact: values });
                    }}
                    errors={getFieldError('di.satContact')}
                  />
                </>
              </Grid>
            </Grid>
          </Panel>
        </>
      )}
      {apEligibilityInd && (
        <>
          <Panel>
            <Grid container spacing={5}>
              <Grid item xs={6}>
                <Title>AP Program Contact Information</Title>
                <>
                  <ContactFields
                    readOnly={readOnly}
                    contact={apContact || {}}
                    onUpdate={(values) => {
                      diChanged({ apContact: values });
                    }}
                    errors={getFieldError('di.apContact')}
                  />
                </>
                <>
                  <div id={`${tooltipId}-ap`}>
                    <FieldRow>
                      <Checkbox
                        disabled={readOnly}
                        onChange={() =>
                          appChanged({
                            apElectronicRprt: !apElectronicRprt,
                          })
                        }
                        label={'Are you interested in more information regarding AP electronic score reporting?'}
                        name={'apElectronicRprt'}
                        checked={apElectronicRprt}
                      />
                    </FieldRow>
                  </div>
                  <Tooltip placement="bottom" trigger={`${tooltipId}-ap`}>
                    <p>
                      {
                        'Electronic Score Reporting will allow you to receive encrypted score data via the internet. By checking this box you will receive an email with more information regarding this option upon application submittal.'
                      }
                    </p>
                  </Tooltip>
                </>
              </Grid>
              <Grid item xs={6}>
                <FieldRow>
                  <Checkbox
                    disabled={readOnly}
                    label={'Is the AP mailing address different from the visiting address?'}
                    style={{ display: 'inline-block' }}
                    checked={!!apAddress}
                    onChange={() => {
                      if (!apAddress) {
                        diChanged({
                          apAddress: {
                            countryIsoCd: (apAddress && apAddress['countryIsoCd']) || 'US',
                            stateCd: (apAddress && apAddress['stateCd']) || undefined,
                            province: (apAddress && apAddress['province']) || undefined,
                          },
                        });
                      } else {
                        diChanged({ apAddress: undefined });
                      }
                    }}
                  />
                  {hasError('di.apAddress') && (
                    <span className="cb-input-helper cb-validation-error">{getError('di.apAddress')}</span>
                  )}
                </FieldRow>
                {apAddress && (
                  <>
                    <FieldRow>
                      <strong>AP Mailing Address</strong>
                    </FieldRow>
                    <AddressFields
                      readOnly={readOnly}
                      addressNote={''}
                      errors={getFieldError('di.apAddress')}
                      address={apAddress}
                      onUpdate={(address) => {
                        diChanged({ apAddress: address });
                      }}
                    />
                  </>
                )}
              </Grid>
            </Grid>
          </Panel>
        </>
      )}
      {/* {clepEligibilityInd && (
        <>
          <Panel>
            <Grid container spacing={5}>
              <Grid item xs={6}>
                <Title>CLEP Program Contact Information</Title>
                <>
                  <ContactFields
                  readOnly={readOnly}
                    item={clepContact || {}}
                    onUpdate={(values) => {
                      diChanged({ clepContact: values });
                    }}
                    errors={getFieldError('di.clepContact')}
                  />
                </>
                <>
                  <div id={`${tooltipId}-clep`}>
                    <FieldRow>
                      <Checkbox
                       disabled={readOnly}
                        onChange={() =>
                          appChanged({
                            clepElectronicRprt: !clepElectronicRprt,
                          })
                        }
                        label={'Are you interested in more information regarding CLEP electronic score reporting?'}
                        name={'clepElectronicRprt'}
                        checked={clepElectronicRprt}
                      />
                    </FieldRow>
                  </div>
                  <Tooltip placement="bottom" trigger={`${tooltipId}-clep`}>
                    <p>
                      {
                        'Electronic Score Reporting will allow you to receive encrypted score data via the internet. By checking this box you will receive an email with more information regarding this option upon application submittal.'
                      }
                    </p>
                  </Tooltip>
                </>
              </Grid>
              <Grid item xs={6}>
                <>
                  <FieldRow>
                    <Checkbox
                     disabled={readOnly}
                      label={'Is the CLEP mailing address different from the visiting address?'}
                      style={{ display: 'inline-block' }}
                      checked={!!clepAddress}
                      onChange={() => {
                        if (!clepAddress) {
                          diChanged({
                            clepAddress: {
                              countryIsoCd: (clepAddress && clepAddress['countryIsoCd']) || 'US',
                              stateCd: clepAddress && clepAddress['stateCd'],
                              province: clepAddress && clepAddress['province'],
                            },
                          });
                        } else {
                          diChanged({ clepAddress: undefined });
                        }
                      }}
                    />
                    {hasError('di.clepAddress') && (
                      <span className="cb-input-helper cb-validation-error">{getError('di.clepAddress')}</span>
                    )}
                  </FieldRow>
                  {clepAddress && (
                    <>
                      <FieldRow>
                        <strong>CLEP Mailing Address</strong>
                      </FieldRow>
                      <AddressFields
                      readOnly={readOnly}
                        removeFieldNote={true}
                        errors={getFieldError('di.clepAddress')}
                        address={clepAddress}
                        onUpdate={(address) => {
                          diChanged({ clepAddress: address });
                        }}
                      />
                    </>
                  )}
                </>
              </Grid>
            </Grid>
          </Panel>
        </>
      )} */}
      <Panel>
        <Grid container spacing={5}>
          <Grid item xs={6}>
            <SupportDocFields
              readOnly={readOnly}
              categoryId={categoryId}
              app={appForm}
              userName={userId || ''}
              errors={errors}
              onUpdate={(app) => {
                changed({ appForm: app });
              }}
            />
          </Grid>
        </Grid>
      </Panel>
    </>
  );
};
